import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, RefreshCw, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import HelpTip from '../ui/HelpTip';
import telegramService, { TelegramLinkStatus } from '../../services/telegramService';
import {
  TELEGRAM_NOTIFY_CATEGORY,
  TelegramNotifyCategory,
} from '../../constants/tradingConstants';

const TELEGRAM_HELP = {
  label: 'Telegram notifications',
  text: 'Get a Telegram message when something needs you — broker login, a rejected order, or an entry or exit that failed — and ask the bot for positions or P&L any time.',
  example: 'Click Connect Telegram → press Start in Telegram → card shows Connected',
  docsUrl: '/help/telegram-notifications.html',
};

// Label + description wording matches the Telegram bot's /alerts listing
// (lambda_functions/telegram/commands.py ALERTS) — keep them in step.
const ALERT_OPTIONS: { category: TelegramNotifyCategory; label: string; description: string }[] = [
  {
    category: TELEGRAM_NOTIFY_CATEGORY.LOGIN_REQUIRED,
    label: 'Broker login required',
    description: "Auto-login failed, or an entry or exit couldn't be placed because your broker is logged out.",
  },
  {
    category: TELEGRAM_NOTIFY_CATEGORY.ORDER_REJECTED,
    label: 'Order rejected / margin shortfall',
    description: 'The broker rejected one of your orders.',
  },
  {
    category: TELEGRAM_NOTIFY_CATEGORY.EXIT_FAILED,
    label: 'Exit could not be placed',
    description: "A position's exit failed, so it is still open.",
  },
  {
    category: TELEGRAM_NOTIFY_CATEGORY.ENTRY_FAILED,
    label: 'Entry could not be placed',
    description: 'A scheduled entry was skipped or the broker kept rejecting it.',
  },
  {
    category: TELEGRAM_NOTIFY_CATEGORY.NEEDS_ATTENTION,
    label: 'Manual check needed',
    description: "A stop-loss or hedge couldn't be placed, or the broker may differ from the app.",
  },
];

/**
 * Self-service "Connect Telegram" card for the Account page. Connecting opens
 * the bot deep-link in a new tab (and always shows it as a link too — Safari/iOS
 * block a tab opened after an await); after the user presses Start in Telegram
 * the webhook binds their chat_id, so we poll link status for ~1 minute to flip
 * the card to "Connected" without a manual refresh.
 *
 * When the Telegram kill switch is off (`available: false`) unlinked users see
 * nothing, and linked users see a "paused" notice with Disconnect.
 */
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_TRIES = 20;
// Used only when the server's reply carries no expires_in (an older backend).
const DEFAULT_LINK_TTL_MS = 15 * 60 * 1000;

const ConnectTelegramCard: React.FC = () => {
  const [status, setStatus] = useState<TelegramLinkStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [deepLink, setDeepLink] = useState<{ url: string; ttlMs: number } | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  // Bumped whenever polling stops, so a poll request still in flight (e.g. one
  // sent just before Disconnect) can't write a stale "linked" status back.
  const pollGenRef = useRef(0);
  // Same idea for load(): only the latest status read may be applied (or its
  // error shown), and a poll that found the link wins over an older load.
  const statusGenRef = useRef(0);
  const mountedRef = useRef(true);

  // Optimistic toggle; reverts on failure. Sends only the toggled category, so
  // a change made from Telegram (/unsubscribe) meanwhile isn't overwritten;
  // the reply is the full stored map.
  const togglePref = async (category: TelegramNotifyCategory, enabled: boolean) => {
    const previous = status?.prefs ?? {};
    setStatus((s) => (s ? { ...s, prefs: { ...previous, [category]: enabled } } : s));
    setSavingPrefs(true);
    setPrefsError(null);
    try {
      const saved = await telegramService.updatePrefs({ [category]: enabled });
      setStatus((s) => (s ? { ...s, prefs: saved } : s));
    } catch (e: any) {
      setStatus((s) => (s ? { ...s, prefs: previous } : s));
      setPrefsError(e?.message || 'Failed to save alert preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollGenRef.current += 1;
    setWaiting(false);
  }, []);

  const load = useCallback(async () => {
    setError(null);
    const gen = ++statusGenRef.current;
    try {
      const s = await telegramService.getStatus();
      if (gen !== statusGenRef.current) return; // superseded by a newer read
      setStatus(s);
      if (s.linked) stopPolling(); // a manual refresh can find the link first
    } catch (e: any) {
      if (gen === statusGenRef.current) setError(e?.message || 'Failed to load Telegram status');
    }
  }, [stopPolling]);

  // Stop offering a link once its token has expired; Connect mints a new one.
  useEffect(() => {
    if (!deepLink) return undefined;
    const timer = window.setTimeout(() => setDeepLink(null), deepLink.ttlMs);
    return () => window.clearTimeout(timer);
  }, [deepLink]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [load, stopPolling]);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const { deep_link, expires_in } = await telegramService.startLink();
      if (!mountedRef.current) return; // left the page while minting: no tab, no poll
      setDeepLink({
        url: deep_link,
        ttlMs: typeof expires_in === 'number' ? expires_in * 1000 : DEFAULT_LINK_TTL_MS,
      });
      // Best effort — may be blocked (Safari/iOS); the link below is the fallback.
      window.open(deep_link, '_blank', 'noopener,noreferrer');
      stopPolling(); // clear any previous poll first — it also resets `waiting`
      setWaiting(true);
      let tries = 0;
      let inFlight = false;
      const gen = pollGenRef.current;
      pollRef.current = window.setInterval(async () => {
        if (inFlight) return; // a slow response: don't stack requests
        inFlight = true;
        tries += 1;
        try {
          const s = await telegramService.getStatus();
          if (gen !== pollGenRef.current) return; // stopped meanwhile
          if (s.linked) {
            statusGenRef.current += 1; // an older load() reply can't undo this
            setStatus(s);
            stopPolling();
            return;
          }
        } catch {
          /* transient — keep polling */
        } finally {
          inFlight = false;
        }
        if (tries >= POLL_MAX_TRIES && gen === pollGenRef.current) stopPolling();
      }, POLL_INTERVAL_MS);
    } catch (e: any) {
      setError(e?.message || 'Failed to start Telegram linking');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    stopPolling(); // first, so no in-flight poll can flip the card back to Connected
    setBusy(true);
    setError(null);
    try {
      await telegramService.unlink();
      setDeepLink(null); // an earlier link is spent; Connect mints a new one
      // preserve `available` so the card stays visible after disconnect
      setStatus((prev) => ({ ...(prev ?? { linked: false }), linked: false }));
    } catch (e: any) {
      setError(e?.message || 'Failed to disconnect Telegram');
    } finally {
      setBusy(false);
    }
  };

  // Until we know status, render nothing (load errors also hide the card). While
  // Telegram is off, only a linked user sees the card — so they can disconnect.
  if (!status) return null; // first load; a refresh keeps the card on screen

  const linked = !!status.linked;
  const paused = !status.available;
  if (paused && !linked) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1.5">
            <CardTitle>Telegram notifications</CardTitle>
            <HelpTip entry={TELEGRAM_HELP} placement="bottom" />
          </div>
          {linked ? (
            <Button variant="secondary" size="sm" onClick={disconnect} disabled={busy}>
              Disconnect
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={connect}
              disabled={busy}
              leftIcon={<Send className="w-4 h-4" />}
            >
              Connect Telegram
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Shown above the body, not instead of it: a failed refresh must not
            take away the deep link (on iOS Safari the only way in). */}
        {error && <div className="mb-3 text-sm text-red-700 dark:text-red-300">{error}</div>}
        {paused ? (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg p-3 text-sm">
            Telegram alerts and bot commands are paused right now. You're still
            connected{status.username ? ` as @${status.username}` : ''}; disconnect if
            you don't want alerts when they resume.
          </div>
        ) : linked ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 text-sm text-green-700 dark:text-green-300">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Connected{status?.username ? ` as @${status.username}` : ''}. You'll receive
                alerts here, and can message the bot <code>/positions</code>, <code>/pnl</code> or{' '}
                <code>/summary</code> any time.
              </span>
            </div>
            <fieldset>
              <legend className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Send me alerts for
              </legend>
              <div className="space-y-3">
                {ALERT_OPTIONS.map(({ category, label, description }) => (
                  <Checkbox
                    key={category}
                    className="flex"
                    checked={status?.prefs?.[category] !== false}
                    disabled={savingPrefs}
                    onChange={(next) => togglePref(category, next)}
                    label={<span className="font-medium">{label}</span>}
                    description={description}
                  />
                ))}
              </div>
              {prefsError && (
                <div className="mt-2 text-xs text-red-700 dark:text-red-300">{prefsError}</div>
              )}
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                You can also manage these in Telegram: send <code>/alerts</code> to the bot, then{' '}
                <code>/subscribe exit</code> or <code>/unsubscribe exit</code> (names: login,
                rejected, exit, entry, attention, or all).
              </p>
            </fieldset>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Connect Telegram to get real-time alerts (broker login needed, order
              rejected, entry or exit failed, manual check needed) and to ask the bot for your positions, P&amp;L or
              end-of-day summary on demand.
            </p>
            {/* Offered for the link's whole lifetime (the server's expires_in), not only
                while polling (60 s) — on iOS Safari it is the only way in. */}
            {deepLink && (
              <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
                {waiting && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                <span>
                  {waiting ? 'Waiting for you to press ' : 'Press '}
                  <strong>Start</strong> in Telegram{waiting ? '…' : ', then refresh.'}{' '}
                  Telegram didn't open?{' '}
                  <a
                    href={deepLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
                  >
                    Open Telegram
                  </a>
                  .{' '}
                  <button type="button" onClick={load} className="underline">
                    refresh
                  </button>
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConnectTelegramCard;
